import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from './firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore'; 
import { Bar, Line, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import './PublicAnalytics.css'; 

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend);

// --- Custom Waffle Chart Component ---
const WaffleChart = React.forwardRef(({ data, colors }, ref) => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return <div style={{textAlign: 'center', padding: '20px'}}>No data available.</div>;

    const waffleBlocks = [];
    let currentBlock = 0;

    data.forEach((item, index) => {
        const percentage = (item.value / total) * 100;
        const numBlocks = Math.round(percentage);
        for (let i = 0; i < numBlocks && currentBlock < 100; i++) {
            waffleBlocks.push({
                label: item.label,
                value: item.value,
                color: colors[index % colors.length]
            });
            currentBlock++;
        }
    });
    
    while (waffleBlocks.length < 100) {
        waffleBlocks.push({ label: 'Rounding', color: '#f0f0f0' });
    }

    return (
        <div className="waffle-chart-container" ref={ref}>
            <div className="waffle-grid">
                {waffleBlocks.map((block, index) => (
                    <div 
                        key={index} 
                        className="waffle-block" 
                        style={{ backgroundColor: block.color }}
                        title={`${block.label}: ${block.value || ''}`}
                    ></div>
                ))}
            </div>
            <div className="waffle-legend">
                {data.map((item, index) => (
                    <div key={item.label} className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: colors[index % colors.length] }}></span>
                        <span className="legend-label">{item.label} ({item.value})</span>
                    </div>
                ))}
            </div>
        </div>
    );
});


const PublicAccidentDetailsModal = ({ accident, onClose }) => {
    const { t } = useTranslation();
    if (!accident) return null;
    const totalInjuries = accident.totalInjuries > 0 ? accident.totalInjuries : (accident.injuriesMale || 0) + (accident.injuriesFemale || 0) + (accident.injuriesChildren || 0);
    const totalFatalities = accident.totalFatalities > 0 ? accident.totalFatalities : (accident.fatalitiesMale || 0) + (accident.fatalitiesFemale || 0) + (accident.fatalitiesChildren || 0);
    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{t('accident_details_title')}</h2>
                    <button onClick={onClose} className="modal-close-btn-top">&times;</button>
                </div>
                <div className="modal-body">
                    <p><strong>{t('date_time_label')}:</strong> {accident.datetime?.toLocaleString()}</p>
                    <p><strong>{t('location_label')}</strong> {`${accident.location}, ${accident.ward}, ${accident.district}, ${accident.region}`}</p><hr />
                    <p><strong>{t('vehicles_involved_label')}</strong></p>
                    <ul>{accident.vehicles?.map((v, i) => (<li key={i}>{v.type}</li>))}</ul><hr />
                    <p><strong>{t('casualties_label')}</strong></p>
                    <ul>
                        <li><strong>{t('total_injuries_label_strong')} {totalInjuries}</strong></li>
                        <li><strong>{t('total_fatalities_label_strong')} {totalFatalities}</strong></li>
                    </ul><hr />
                    <p><strong>{t('description_label_strong')}</strong></p><p className="description-box">{accident.description || t('no_description_provided')}</p>
                    
                    {/* --- CORRECTED: Added Media Gallery Display --- */}
                    {accident.mediaUrls && accident.mediaUrls.length > 0 && (
                        <>
                            <hr />
                            <p><strong>{t('media_label', 'Media')}:</strong></p>
                            <div className="media-gallery">
                                {accident.mediaUrls.map((url, index) => (
                                    <a key={index} href={url} target="_blank" rel="noopener noreferrer">
                                        <img src={url} alt={`${t('accident_media_alt_text', 'Accident Media')} ${index + 1}`} />
                                    </a>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div className="modal-footer">
                    <button onClick={onClose} className="modal-close-btn">{t('close_button')}</button>
                </div>
            </div>
        </div>
    );
};

function PublicAnalytics({ theme }) {
    const { t } = useTranslation();
    const [accidents, setAccidents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedAccident, setSelectedAccident] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'datetime', direction: 'descending' });
    
    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);

    const timeChartRef = useRef(null);
    const vehicleChartRef = useRef(null);
    const regionChartRef = useRef(null);
    const districtChartRef = useRef(null);
    const casualtyChartRef = useRef(null);
    const topRegionsChartRef = useRef(null);
    const waffleChartRef = useRef(null);

    const donationLink = "https://gofund.me/93585663";
    
    const driverTips = ['safety_tip_seatbelt', 'safety_tip_no_speed', 'safety_tip_no_distractions', 'safety_tip_no_drink_drive', 'safety_tip_vehicle_maintenance', 'safety_tip_reflector', 'safety_tip_extinguisher'];
    const motoristTips = ['safety_tip_helmet', 'safety_tip_passenger_helmet', 'safety_tip_no_overload', 'safety_tip_indicators', 'safety_tip_reflector', 'safety_tip_extinguisher'];
    const pedestrianTips = ['safety_tip_crosswalk', 'safety_tip_look_both_ways', 'safety_tip_be_visible', 'safety_tip_no_headphones'];

    useEffect(() => {
        const accidentsRef = collection(db, 'accidents');
        const q = query(accidentsRef, where("isVerified", "==", true));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const accidentsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), datetime: doc.data().datetime?.toDate() }));
            setAccidents(accidentsData);
            setIsLoading(false);
        }, (error) => {
            console.error("Error fetching public analytics data: ", error);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const sortedAndFilteredAccidents = useMemo(() => {
        let sortableItems = [...accidents];
        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            sortableItems = sortableItems.filter(accident =>
                (accident.location?.toLowerCase().includes(lowercasedTerm)) ||
                (accident.region?.toLowerCase().includes(lowercasedTerm)) ||
                (accident.district?.toLowerCase().includes(lowercasedTerm)) ||
                (accident.ward?.toLowerCase().includes(lowercasedTerm))
            );
        }
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                if (sortConfig.key === 'totalInjuries') {
                    aValue = (a.totalInjuries > 0) ? a.totalInjuries : (a.injuriesMale || 0) + (a.injuriesFemale || 0) + (a.injuriesChildren || 0);
                    bValue = (b.totalInjuries > 0) ? b.totalInjuries : (b.injuriesMale || 0) + (b.injuriesFemale || 0) + (b.injuriesChildren || 0);
                }
                 if (sortConfig.key === 'totalFatalities') {
                    aValue = (a.totalFatalities > 0) ? a.totalFatalities : (a.fatalitiesMale || 0) + (a.fatalitiesFemale || 0) + (a.fatalitiesChildren || 0);
                    bValue = (b.totalFatalities > 0) ? b.totalFatalities : (b.fatalitiesMale || 0) + (b.fatalitiesFemale || 0) + (b.fatalitiesChildren || 0);
                }
                if (aValue < bValue) { return sortConfig.direction === 'ascending' ? -1 : 1; }
                if (aValue > bValue) { return sortConfig.direction === 'ascending' ? 1 : -1; }
                return 0;
            });
        }
        return sortableItems;
    }, [accidents, searchTerm, sortConfig]);

    const paginatedAccidents = useMemo(() => {
        const startIndex = (currentPage - 1) * entriesPerPage;
        const endIndex = startIndex + entriesPerPage;
        return sortedAndFilteredAccidents.slice(startIndex, endIndex);
    }, [sortedAndFilteredAccidents, currentPage, entriesPerPage]);

    const totalPages = Math.ceil(sortedAndFilteredAccidents.length / entriesPerPage);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <span className="sort-indicator">&nbsp;</span>;
        return <span className="sort-indicator">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>;
    };
    
    const truncate = (str, n) => {
        if (!str) return '';
        return str.length > n ? str.slice(0, n - 1) + '...' : str;
    };

    const exportToCSV = () => {
        const headers = [t('date_header'), t('location_header'), t('district_header'), t('region_header'), t('injuries_header'), t('deaths_header'), "Description"];
        const rows = sortedAndFilteredAccidents.map(acc => {
            const totalInjuries = acc.totalInjuries > 0 ? acc.totalInjuries : (acc.injuriesMale || 0) + (acc.injuriesFemale || 0) + (acc.injuriesChildren || 0);
            const totalFatalities = acc.totalFatalities > 0 ? acc.totalFatalities : (acc.fatalitiesMale || 0) + (acc.fatalitiesFemale || 0) + (acc.fatalitiesChildren || 0);
            const description = `"${(acc.description || "").replace(/"/g, '""')}"`;
            return [acc.datetime?.toLocaleDateString() || 'N/A', acc.location || 'N/A', acc.district || 'N/A', acc.region || 'N/A', totalInjuries, totalFatalities, description].join(',');
        });
        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "trard_reports.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text(t('analytics_title'), 14, 16);
        autoTable(doc, {
            startY: 22,
            head: [[t('date_header'), t('location_header'), t('district_header'), t('region_header'), t('injuries_header'), t('deaths_header')]],
            body: sortedAndFilteredAccidents.map(acc => [
                acc.datetime?.toLocaleDateString() || 'N/A',
                acc.location || 'N/A',
                acc.district || 'N/A',
                acc.region || 'N/A',
                acc.totalInjuries > 0 ? acc.totalInjuries : (acc.injuriesMale || 0) + (acc.injuriesFemale || 0) + (acc.injuriesChildren || 0),
                acc.totalFatalities > 0 ? acc.totalFatalities : (acc.fatalitiesMale || 0) + (acc.fatalitiesFemale || 0) + (acc.fatalitiesChildren || 0),
            ]),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [22, 160, 133] }
        });
        doc.save('trard_reports.pdf');
    };

    const exportChart = (chartRef, fileName) => {
        const chart = chartRef.current;
        if (!chart) return;
    
        const originalOptions = JSON.parse(JSON.stringify(chart.options));
        const whiteColor = '#FFFFFF';
        const transparentGrid = 'rgba(255, 255, 255, 0.25)';
    
        // Safely update colors
        if (chart.options.plugins && chart.options.plugins.legend) {
            chart.options.plugins.legend.labels.color = whiteColor;
        }
        if (chart.options.scales) {
            if (chart.options.scales.x) {
                if (chart.options.scales.x.ticks) chart.options.scales.x.ticks.color = whiteColor;
                if (chart.options.scales.x.title) chart.options.scales.x.title.color = whiteColor;
                if (chart.options.scales.x.grid) chart.options.scales.x.grid.color = transparentGrid;
            }
            if (chart.options.scales.y) {
                if (chart.options.scales.y.ticks) chart.options.scales.y.ticks.color = whiteColor;
                if (chart.options.scales.y.title) chart.options.scales.y.title.color = whiteColor;
                if (chart.options.scales.y.grid) chart.options.scales.y.grid.color = transparentGrid;
            }
        }
        chart.update('none');
    
        setTimeout(() => {
            const image = chart.toBase64Image();
            const link = document.createElement('a');
            link.href = image;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
    
            // Revert to original options
            chart.options = originalOptions;
            chart.update('none');
        }, 250);
    };

    const exportWaffleChart = (fileName) => {
        if (waffleChartRef.current) {
            const legendLabels = waffleChartRef.current.querySelectorAll('.legend-label');
            const originalColors = Array.from(legendLabels).map(label => label.style.color);
            legendLabels.forEach(label => label.style.color = 'white');

            html2canvas(waffleChartRef.current, { backgroundColor: null }).then(canvas => {
                const image = canvas.toDataURL('image/png');
                const link = document.createElement('a');
                link.href = image;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                legendLabels.forEach((label, index) => label.style.color = originalColors[index]);
            });
        }
    };

    const chartOptions = useMemo(() => {
        const isDark = theme === 'dark';
        const textColor = isDark ? '#e9ecef' : '#495057';
        const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
        return {
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { labels: { color: textColor } }, title: { display: true, color: textColor } },
            scales: { x: { ticks: { color: textColor }, grid: { color: gridColor }, title: { color: textColor, display: true } }, y: { ticks: { color: textColor }, grid: { color: gridColor }, title: { color: textColor, display: true } } }
        };
    }, [theme]);
    
    const timeChartData = useMemo(() => {
        const counts = accidents.reduce((acc, curr) => {
            if (curr.datetime) {
                const month = curr.datetime.toLocaleString('en-US', { month: 'short', year: 'numeric' });
                acc[month] = (acc[month] || 0) + 1;
            } return acc;
        }, {});
        const sortedLabels = Object.keys(counts).sort((a, b) => new Date(a) - new Date(b));
        return { labels: sortedLabels, datasets: [{ label: t('chart_label_accidents_per_month'), data: sortedLabels.map(label => counts[label]), borderColor: 'rgb(75, 192, 192)', backgroundColor: 'rgba(75, 192, 192, 0.5)' }] };
    }, [accidents, t]);

    const sourceChartData = useMemo(() => {
        const counts = accidents.reduce((acc, curr) => {
            const source = curr.source || 'Unknown';
            acc[source] = (acc[source] || 0) + 1;
            return acc;
        }, {});
        return Object.entries(counts).map(([key, value]) => ({ label: key, value }));
    }, [accidents]);

    const casualtyByRegionChartData = useMemo(() => {
        const data = accidents.reduce((acc, curr) => {
            const region = curr.region || 'Unknown';
            if (!acc[region]) acc[region] = { injuries: 0, fatalities: 0 };
            acc[region].injuries += (curr.totalInjuries > 0) ? curr.totalInjuries : (curr.injuriesMale || 0) + (curr.injuriesFemale || 0) + (curr.injuriesChildren || 0);
            acc[region].fatalities += (curr.totalFatalities > 0) ? curr.totalFatalities : (curr.fatalitiesMale || 0) + (curr.fatalitiesFemale || 0) + (curr.fatalitiesChildren || 0);
            return acc;
        }, {});
        const labels = Object.keys(data);
        return {
            labels,
            datasets: [
                { label: t('chart_label_total_injuries'), data: labels.map(label => data[label].injuries), backgroundColor: 'rgba(255, 159, 64, 0.7)' },
                { label: t('chart_label_total_fatalities'), data: labels.map(label => data[label].fatalities), backgroundColor: 'rgba(255, 99, 132, 0.7)' },
            ]
        };
    }, [accidents, t]);
    
    const districtHistogramData = useMemo(() => {
        const counts = accidents.reduce((acc, curr) => {
            if (curr.district) acc[curr.district] = (acc[curr.district] || 0) + 1;
            return acc;
        }, {});
        const sortedDistricts = Object.entries(counts).sort(([,a],[,b]) => b-a).slice(0, 10);
        return { labels: sortedDistricts.map(d => d[0]), datasets: [{ label: t('chart_label_top_10_districts'), data: sortedDistricts.map(d => d[1]), backgroundColor: 'rgba(153, 102, 255, 0.6)' }] };
    }, [accidents, t]);

    const vehicleChartData = useMemo(() => {
        const counts = accidents.reduce((acc, curr) => {
            curr.vehicles?.forEach(vehicle => {
                const type = typeof vehicle === 'string' ? vehicle : (vehicle.type || 'Unknown');
                acc[type] = (acc[type] || 0) + 1;
            });
            return acc;
        }, {});
        const sortedVehicles = Object.entries(counts).sort(([,a],[,b]) => a-b);
        return { 
            labels: sortedVehicles.map(v => v[0]), 
            datasets: [{ 
                label: t('vehicles_involved_chart'),
                data: sortedVehicles.map(v => v[1]), 
                backgroundColor: '#36A2EB',
                borderColor: '#257cb6',
                borderWidth: 1
            }] 
        };
    }, [accidents, t]);

    const injuriesVsDeathsChartData = useMemo(() => {
        const totalInjuries = accidents.reduce((sum, acc) => sum + ((acc.totalInjuries > 0) ? acc.totalInjuries : (acc.injuriesMale || 0) + (acc.injuriesFemale || 0) + (acc.injuriesChildren || 0)), 0);
        const totalFatalities = accidents.reduce((sum, acc) => sum + ((acc.totalFatalities > 0) ? acc.totalFatalities : (acc.fatalitiesMale || 0) + (acc.fatalitiesFemale || 0) + (acc.fatalitiesChildren || 0)), 0);
        return { labels: [t('injuries_heading'), t('deaths_heading')], datasets: [{ data: [totalInjuries, totalFatalities], backgroundColor: ['rgba(255, 159, 64, 0.8)', 'rgba(255, 99, 132, 0.8)'], hoverOffset: 4 }] };
    }, [accidents, t]);
    
    const regionHistogramData = useMemo(() => {
        const counts = accidents.reduce((acc, curr) => {
            if (curr.region) acc[curr.region] = (acc[curr.region] || 0) + 1;
            return acc;
        }, {});
        const sortedRegions = Object.entries(counts).sort(([,a],[,b]) => b-a).slice(0, 10);
        return { labels: sortedRegions.map(r => r[0]), datasets: [{ label: t('chart_label_top_10_regions'), data: sortedRegions.map(r => r[1]), backgroundColor: 'rgba(54, 162, 235, 0.6)' }] };
    }, [accidents, t]);

    if (isLoading) {
        return <div className="loading-screen">Loading Analytics...</div>;
    }

    return (
        <div className="public-analytics-container">
            <style>{`
                .waffle-chart-container { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; }
                .waffle-grid { display: grid; grid-template-columns: repeat(10, 1fr); grid-template-rows: repeat(10, 1fr); width: 90%; height: auto; aspect-ratio: 1 / 1; max-width: 250px; border: 1px solid #ccc; gap: 2px; padding: 2px; }
                .waffle-block { width: 100%; height: 100%; }
                .waffle-legend { display: flex; flex-wrap: wrap; justify-content: center; margin-top: 15px; }
                .legend-item { display: flex; align-items: center; margin: 0 10px 5px 10px; }
                .legend-color { width: 15px; height: 15px; margin-right: 5px; border-radius: 3px; }
                .legend-label { font-size: 0.8rem; }
            `}</style>
            {selectedAccident && <PublicAccidentDetailsModal accident={selectedAccident} onClose={() => setSelectedAccident(null)} />}
            
            <div className="page-header">
                <h1>{t('analytics_title')}</h1>
                <p>{t('analytics_intro_detailed')}</p>
                <a href={donationLink} target="_blank" rel="noopener noreferrer" className="donate-btn header-donate-btn">
                    {t('support_our_project_button')}
                </a>
            </div>

            <div className="charts-grid">
                <div className="chart-card large-card">
                    <div className="chart-header">
                        <h3>{t('accidents_over_time_chart')}</h3>
                        <button onClick={() => exportChart(timeChartRef, 'accidents_over_time.png')} className="export-chart-btn">{t('export_chart_button')}</button>
                    </div>
                    <div className="chart-wrapper"><Line ref={timeChartRef} data={timeChartData} options={{...chartOptions, plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, display: false }}}} /></div>
                </div>
                <div className="chart-card">
                    <div className="chart-header">
                        <h3>{t('report_sources_chart')}</h3>
                        <button onClick={() => exportWaffleChart('report_sources.png')} className="export-chart-btn">{t('export_chart_button')}</button>
                    </div>
                    <div className="chart-wrapper">
                        <WaffleChart ref={waffleChartRef} data={sourceChartData} colors={['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40']} />
                    </div>
                </div>
                <div className="chart-card">
                     <div className="chart-header">
                        <h3>{t('injuries_vs_deaths_chart')}</h3>
                        <button onClick={() => exportChart(casualtyChartRef, 'injuries_vs_deaths.png')} className="export-chart-btn">{t('export_chart_button')}</button>
                    </div>
                    <div className="chart-wrapper"><Pie ref={casualtyChartRef} data={injuriesVsDeathsChartData} options={{...chartOptions, plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, display: false }}}} /></div>
                </div>
                <div className="chart-card large-card">
                    <div className="chart-header">
                        <h3>{t('casualties_by_region_chart')}</h3>
                        <button onClick={() => exportChart(regionChartRef, 'casualties_by_region.png')} className="export-chart-btn">{t('export_chart_button')}</button>
                    </div>
                    <div className="chart-wrapper"><Bar ref={regionChartRef} data={casualtyByRegionChartData} options={{...chartOptions, indexAxis: 'y', plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, display: false }}}} /></div>
                </div>
                <div className="chart-card">
                    <div className="chart-header">
                        <h3>{t('vehicles_involved_chart')}</h3>
                        <button onClick={() => exportChart(vehicleChartRef, 'vehicles_involved.png')} className="export-chart-btn">{t('export_chart_button')}</button>
                    </div>
                    <div className="chart-wrapper">
                        <Bar 
                            ref={vehicleChartRef} 
                            data={vehicleChartData} 
                            options={{...chartOptions, indexAxis: 'y', plugins: { ...chartOptions.plugins, legend: { display: false }, title: { ...chartOptions.plugins.title, display: false }}}} 
                        />
                    </div>
                </div>
                <div className="chart-card">
                     <div className="chart-header">
                        <h3>{t('top_10_districts_chart')}</h3>
                        <button onClick={() => exportChart(districtChartRef, 'top_10_districts.png')} className="export-chart-btn">{t('export_chart_button')}</button>
                    </div>
                    <div className="chart-wrapper"><Bar ref={districtChartRef} data={districtHistogramData} options={{...chartOptions, plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, display: false }}}} /></div>
                </div>
                <div className="chart-card large-card">
                     <div className="chart-header">
                        <h3>{t('top_10_regions_chart')}</h3>
                        <button onClick={() => exportChart(topRegionsChartRef, 'top_10_regions.png')} className="export-chart-btn">{t('export_chart_button')}</button>
                    </div>
                    <div className="chart-wrapper"><Bar ref={topRegionsChartRef} data={regionHistogramData} options={{...chartOptions, plugins: { ...chartOptions.plugins, title: { ...chartOptions.plugins.title, display: false }}}} /></div>
                </div>
            </div>

            <div className="reports-list-section">
                <div className="section-header">
                    <h2>{t('recent_verified_reports_heading')}</h2>
                    <div className="export-buttons">
                        <button onClick={exportToCSV} className="export-btn">{t('export_csv_button')}</button>
                        <button onClick={exportToPDF} className="export-btn">{t('export_pdf_button')}</button>
                    </div>
                </div>
                <div className="public-search-container">
                    <input type="text" placeholder={t('search_reports_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="public-search-bar" />
                </div>
                <div className="public-table-container">
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('datetime')} className="sortable">{t('date_header')} {getSortIndicator('datetime')}</th>
                                <th onClick={() => requestSort('location')} className="sortable">{t('location_header')} {getSortIndicator('location')}</th>
                                <th onClick={() => requestSort('district')} className="sortable">{t('district_header')} {getSortIndicator('district')}</th>
                                <th onClick={() => requestSort('region')} className="sortable">{t('region_header')} {getSortIndicator('region')}</th>
                                <th onClick={() => requestSort('totalInjuries')} className="sortable">{t('injuries_header')} {getSortIndicator('totalInjuries')}</th>
                                <th onClick={() => requestSort('totalFatalities')} className="sortable">{t('deaths_header')} {getSortIndicator('totalFatalities')}</th>
                                <th>{t('details_header')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedAccidents.map(accident => {
                                const totalInjuries = accident.totalInjuries > 0 ? accident.totalInjuries : (accident.injuriesMale || 0) + (accident.injuriesFemale || 0) + (accident.injuriesChildren || 0);
                                const totalFatalities = accident.totalFatalities > 0 ? accident.totalFatalities : (accident.fatalitiesMale || 0) + (accident.fatalitiesFemale || 0) + (accident.fatalitiesChildren || 0);
                                return (
                                    <tr key={accident.id} onClick={() => setSelectedAccident(accident)} className="clickable-row">
                                        <td data-label={t('date_header')}>{accident.datetime?.toLocaleDateString()}</td>
                                        <td data-label={t('location_header')} title={accident.location}>{truncate(accident.location, 30)}</td>
                                        <td data-label={t('district_header')} title={accident.district}>{truncate(accident.district, 20)}</td>
                                        <td data-label={t('region_header')}>{accident.region}</td>
                                        <td data-label={t('injuries_header')}>{totalInjuries}</td>
                                        <td data-label={t('deaths_header')}>{totalFatalities}</td>
                                        <td data-label={t('details_header')}><button className="btn-details-public">{t('view_button')}</button></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
                    <div className="page-navigation">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>{t('previous_button')}</button>
                        <span>{t('page_label')} {currentPage} {t('of_label')} {totalPages}</span>
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>{t('next_button')}</button>
                    </div>
                    <div className="entries-per-page">
                        <label>{t('entries_per_page_label')}</label>
                        <select value={entriesPerPage} onChange={(e) => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                            <option value={10}>10</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="donate-section">
                <h2>{t('support_our_mission_heading')}</h2>
                <p>{t('support_our_mission_text')}</p>
                <a href={donationLink} target="_blank" rel="noopener noreferrer" className="donate-btn">
                    {t('donate_now_button')}
                </a>
            </div>

            <div className="safety-tips-section">
                <h2>{t('road_safety_awareness_heading')}</h2>
                <div className="tips-grid">
                    <div className="tip-card">
                        <h4>{t('for_drivers_heading')}</h4>
                        <ul>{driverTips.map(tip => <li key={tip}>{t(tip)}</li>)}</ul>
                    </div>
                    <div className="tip-card">
                        <h4>{t('for_motorcyclists_heading')}</h4>
                        <ul>{motoristTips.map(tip => <li key={tip}>{t(tip)}</li>)}</ul>
                    </div>
                    <div className="tip-card">
                        <h4>{t('for_pedestrians_heading')}</h4>
                        <ul>{pedestrianTips.map(tip => <li key={tip}>{t(tip)}</li>)}</ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PublicAnalytics;
